"use client";

import Link from "next/link";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";

import type { NavCategory } from "../categories";

type Props = {
  categories: NavCategory[];
  className?: string;
};

export function NavBar({ categories, className }: Props) {
  return (
    <NavigationMenu
      viewport={false}
      className={cn("max-w-none", className)}
    >
      <NavigationMenuList className="-ml-3 gap-0 justify-start">
        {categories.map((category) => (
          <NavigationMenuItem key={category.name}>
            {category.type === "leaf" ? (
              <NavigationMenuLink asChild>
                <Link
                  href={category.href}
                  className="rounded-none border-b-2 border-b-transparent bg-transparent px-3 text-[14px] font-normal text-[#161616] shadow-none hover:bg-transparent! hover:text-primary focus:bg-transparent focus-visible:rounded-sm focus-visible:border-b-primary focus-visible:text-primary"
                >
                  {category.name}
                </Link>
              </NavigationMenuLink>
            ) : (
              <>
                <NavigationMenuTrigger className="h-12 gap-1 rounded-none border-b-2 border-b-transparent bg-transparent px-3 text-[14px] font-normal text-[#161616] shadow-none hover:bg-transparent! hover:text-primary focus:bg-transparent focus-visible:rounded-sm focus-visible:border-b-primary focus-visible:text-primary data-[state=open]:border-b-primary data-[state=open]:bg-transparent data-[state=open]:text-primary data-[state=open]:shadow-none">
                  {category.name}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="top-0! w-full! p-4 data-[motion^=from-]:animate-none! data-[motion^=to-]:animate-none!">
                  <div className="flex items-center justify-between mb-2">
                    <span className="ps-2 text-[13px] font-extrabold text-[#161616] uppercase tracking-wide">
                      {category.name}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-x-10 gap-y-2">
                    {category.children.map((child) => (
                      <NavigationMenuLink
                        key={child.href}
                        asChild
                      >
                        <Link
                          href={child.href}
                          className="flex-col gap-0 rounded-none text-[14px] font-semibold text-[#161616] capitalize transition-colors hover:bg-transparent! hover:text-primary! focus-visible:text-primary"
                        >
                          {child.name}
                        </Link>
                      </NavigationMenuLink>
                    ))}
                  </div>
                </NavigationMenuContent>
              </>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>

      <NavigationMenuViewport
        wrapperClassName="!fixed !top-[var(--header-height)] left-0 right-0"
        className="mt-0 md:w-page-width bg-transparent border-0 border-transparent overflow-hidden duration-300 data-[state=open]:animate-none data-[state=closed]:animate-none"
      />
    </NavigationMenu>
  );
}
